import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, filter, mergeMap, noop, of, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { StoreChangesService } from 'projects/shared/services/store-changes.service';
import { SideMenuService } from '../layout/side-menu/services/side-menu.service';
import { UserService } from 'projects/shared/services/user.service';
import { ActivitiesService } from '../panel/activities/services/activities.service';
import { ActivityLogRes } from '../panel/activities/interfaces/activities';
import { Router } from '@angular/router';
import { makeQueryParams } from '../../shared/helpers/makeQueryParams';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  loginMessageAfter!: string;
  endsubs$: Subject<any> = new Subject();

  topUpOrdersCount: any = 0;
  topUpProcessingCount = 0;

  ordersCount: any = 0;
  orderProcessingCount = 0;

  userCounts = 0;

  activitiesLog!: ActivityLogRes;

  ticketCounts!: { open: number; closed: number };

  moduleColorMap: { [key: string]: string } = {
    Categories: 'categories',
    Products: 'products',
    Users: 'users',
    Auth: 'users',
    Wallets: 'wallets',
    Orders: 'orders',
    Staff: 'staff',
    Tickets: 'tickets',
    Notifications: 'notifications',
    Payments: 'revenue',
    Suppliers: 'suppliers',
    Misc: 'default',
    Blogs: 'blogs',
    FAQs: 'default',
    Licenses: 'licenses',
    Social_Media: 'default',
    Web_Setting: 'default',
    Maintenance: 'danger',
    Target_Alert: 'danger',
    Top_Up_Link: 'wallets',
    Manual_Payment: 'revenue',
  };

  actionBadgeMap: { [key: string]: string } = {
    CREATE: 'badge-approved',
    UPDATE: 'badge-pending',
    DELETE: 'badge-danger',
    SORT: 'badge-processing',
    REDEEM: 'badge-approved',
    LOGIN: 'badge-approved',
    LOGOUT: 'badge-pending',
    CONFIRM: 'badge-approved',
    FAILED_ATTEMPT: 'badge-danger',
    ACCESS_SENSITIVE: 'badge-danger',
    READ: 'badge-pending',
    PROCESS: 'badge-processing',
    RESTORE: 'badge-approved',
    SELL: 'badge-approved',
    EXPIRE: 'badge-danger',
    TOGGLE: 'badge-processing',
  };

  private _subscription = new Subscription();

  constructor(
    private _messageService: MessageService,
    private _storeChangesServ: StoreChangesService,
    private _sideMenuService: SideMenuService,
    private _userService: UserService,
    private _activitiesService: ActivitiesService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    this.loginMessage();
    this.getTopUpOrdersCount();
    this.getOrdersCount();
    this.getUsersCount();
    this.getTicketCount();
    this.getActivityLog();
  }

  ngAfterViewInit(): void {
    if (this.loginMessageAfter != '') {
      this._messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: this.loginMessageAfter,
      });
      this._storeChangesServ.setloginMessage('');
    }
    document.addEventListener('click', (event: any) => {
      if (event.target.matches('a[routerlink]')) {
        event.preventDefault();
        const href = event.target.getAttribute('routerlink');
        this._router.navigate([href]);
      }
    });

  }

  getUsersCount() {
    this._subscription.add(
      this._userService
        .getAllUsers(makeQueryParams({ page: 1, limit: 1 }))
        .pipe(
          mergeMap((count) => of(count.total)),
          tap((res) => {
            this.userCounts = res;
          }),
        )
        .subscribe(noop),
    );
  }

  getTopUpOrdersCount() {
    this._sideMenuService.topUpsCount_BS
      .pipe(
        filter((count) => !!count),
        tap((count) => {
          this.topUpProcessingCount = count?.['Processing'];
          this.topUpOrdersCount = Object.values(count).reduce(
            (sum: any, count: any) => sum + count,
            0,
          );
        }),
      )
      .subscribe(noop);
  }

  getOrdersCount() {
    this._sideMenuService.orderCount_BS
      .pipe(
        filter((count) => !!count),
        tap((count) => {
          this.orderProcessingCount = count;
          this.ordersCount = count.reduce((total: number, item: any) => {
            if (item._id?.toLowerCase() === 'processing') {
              this.orderProcessingCount = item.count;
            }
            return total + item.count;
          }, 0);
        }),
      )
      .subscribe(noop);
  }

  getTicketCount() {
    this._subscription.add(
      this._sideMenuService.ticketCount_BS
        .pipe(
          filter((count) => !!count),
          tap((count) => {
            this.ticketCounts = count;
          }),
        )
        .subscribe(noop),
    );
  }

  loginMessage() {
    this._storeChangesServ.getloginMessage().subscribe((res) => {
      this.loginMessageAfter = res;
    });
  }

  getActivityLog() {
    this._subscription.add(
      this._activitiesService
        .getActivityLog(1, 5)
        .pipe(
          tap((res) => {
            this.activitiesLog = res;
            console.log(this.activitiesLog.data);
          }),
        )
        .subscribe(noop),
    );
  }

  onActivityClick(event: Event, activity: any) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('response-anchor')) {
      event.preventDefault();
      const userId = target.getAttribute('data-user-id');
      if (userId) {
        this._router.navigate(['/panel/users/usersList', userId]);
      }
    }
  }

  getModuleClass(module: string): string {
    return this.moduleColorMap[module] || 'default';
  }

  getActionBadgeClass(action: string): string {
    return this.actionBadgeMap[action] || 'badge-processing';
  }

  getTimeAgo(date: string): string {
    const diffMs = Date.now() - new Date(date).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
